import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ShoppingBag } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useCart } from "@/lib/cart";
import { NAV } from "@/constants/testIds";
import { ASSETS } from "@/lib/assets";

const links = [
  { id: "home", to: "#home", key: "home", testid: NAV.home },
  { id: "about", to: "#about", key: "about", testid: NAV.about },
  { id: "wines", to: "#wines", key: "wines", testid: NAV.wines },
  { id: "shop", to: "#shop", key: "shop", testid: NAV.shop },
  { id: "contact", to: "#contact", key: "contact", testid: NAV.contact },
];

export default function Nav() {
  const { t, lang, toggle } = useI18n();
  const { cartCount, setDrawerOpen } = useCart();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [bump, setBump] = useState(0);

  useEffect(() => {
    if (cartCount > 0) setBump((b) => b + 1);
  }, [cartCount]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid={NAV.bar}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#0a0a0a]/85 backdrop-blur-md border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10 h-20 flex items-center justify-between">
        <a href="#home" data-testid={NAV.logo} className="flex items-center gap-3 group">
          <img src={ASSETS.logo} alt="Stanimirović" className="h-10 w-10 object-contain" />
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="font-serif text-[15px] tracking-[0.18em] text-white/95">STANIMIROVIĆ</span>
            <span className="text-[10px] tracking-[0.34em] text-white/45">PORODIČNA VINARIJA</span>
          </div>
        </a>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <a
              key={l.id}
              href={l.to}
              data-testid={l.testid}
              className="relative text-[12px] tracking-[0.28em] uppercase text-white/65 hover:text-white transition-colors duration-300"
            >
              {t.nav[l.key]}
              <span className="absolute -bottom-1 left-0 w-0 h-px bg-[#c5a572] transition-all duration-500 hover:w-full" />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <motion.button
            data-testid="nav-cart-button"
            onClick={() => setDrawerOpen(true)}
            animate={
              bump > 0
                ? { scale: [1, 1.18, 0.96, 1.06, 1], rotate: [0, -8, 6, -3, 0] }
                : { scale: 1, rotate: 0 }
            }
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            key={bump}
            className="relative inline-flex items-center gap-2 text-white/75 hover:text-[#c5a572] border border-white/15 hover:border-[#c5a572] px-3.5 py-1.5 rounded-full transition-colors duration-300"
            aria-label="cart"
          >
            <ShoppingBag size={14} />
            <span className="hidden sm:inline text-[11px] tracking-[0.28em] uppercase">
              {t.nav.cart}
            </span>
            <AnimatePresence mode="popLayout">
              <motion.span
                key={cartCount}
                data-testid="nav-cart-count"
                initial={{ scale: 0.4, opacity: 0, y: -6 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.4, opacity: 0, y: 6 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                className={`min-w-[20px] h-5 px-1.5 flex items-center justify-center rounded-full text-[10px] font-semibold ${
                  cartCount > 0
                    ? "bg-[#c5a572] text-[#0a0a0a]"
                    : "bg-white/10 text-white/60"
                }`}
              >
                {cartCount}
              </motion.span>
            </AnimatePresence>
            {/* Pulse ring on add */}
            <AnimatePresence>
              {bump > 0 && (
                <motion.span
                  key={`ring-${bump}`}
                  initial={{ opacity: 0.6, scale: 0.8 }}
                  animate={{ opacity: 0, scale: 1.6 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.7, ease: "easeOut" }}
                  className="absolute inset-0 rounded-full border border-[#c5a572] pointer-events-none"
                />
              )}
            </AnimatePresence>
          </motion.button>
          <button
            data-testid={NAV.langToggle}
            onClick={toggle}
            className="text-[11px] tracking-[0.28em] uppercase text-white/65 hover:text-white border border-white/15 hover:border-[#c5a572] px-3 py-1.5 rounded-full transition-all duration-300"
          >
            {lang === "sr" ? "EN" : "SR"}
          </button>
          <button
            data-testid={NAV.mobileToggle}
            onClick={() => setOpen((o) => !o)}
            className="md:hidden text-white/80 hover:text-white"
            aria-label="menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-[#0a0a0a] border-t border-white/5">
          <nav className="flex flex-col px-6 py-6 gap-5">
            {links.map((l) => (
              <a
                key={l.id}
                href={l.to}
                onClick={() => setOpen(false)}
                className="text-[13px] tracking-[0.28em] uppercase text-white/75 hover:text-white"
              >
                {t.nav[l.key]}
              </a>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}
